/*
  Trading as a Service — interaction layer. Loaded once from
  sections/taas-sticky-nav.liquid, which only renders on templates/page.taas.json.

  Everything here is progressive: all page content is rendered server-side by
  Liquid and is visible with this file blocked or failing. The reveal animation
  fails open, the accordions and tab panels start in a readable state, and the
  counters print their final value as static markup.
*/
(function () {
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- reveal ---------- */
  var revealables = document.querySelectorAll('[data-t-reveal]');

  if ('IntersectionObserver' in window && !reduced) {
    var revealObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('t-in');
          revealObserver.unobserve(entry.target);
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
    );
    revealables.forEach(function (el) {
      revealObserver.observe(el);
    });
  } else {
    revealables.forEach(function (el) {
      el.classList.add('t-in');
    });
  }

  /* ---------- scroll progress ---------- */
  var progress = document.querySelector('[data-t-progress]');

  if (progress) {
    var setProgress = function () {
      var scrollable = document.documentElement.scrollHeight - window.innerHeight;
      progress.style.width = (scrollable > 0 ? (window.scrollY / scrollable) * 100 : 0) + '%';
    };
    setProgress();
    window.addEventListener('scroll', setProgress, { passive: true });
    window.addEventListener('resize', setProgress, { passive: true });
  }

  /* ---------- sticky nav: show on scroll, mark the active chapter ---------- */
  var nav = document.querySelector('[data-t-nav]');

  if (nav) {
    var revealAfter = parseInt(nav.getAttribute('data-t-nav-offset'), 10) || 400;
    var setNavVisible = function () {
      nav.classList.toggle('t-nav--visible', window.scrollY > revealAfter);
    };
    setNavVisible();
    window.addEventListener('scroll', setNavVisible, { passive: true });

    var navLinks = Array.prototype.slice.call(nav.querySelectorAll('[data-t-nav-link]'));
    var targets = navLinks
      .map(function (link) {
        return document.querySelector(link.getAttribute('href'));
      })
      .filter(Boolean);

    if (targets.length && 'IntersectionObserver' in window) {
      var navObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            navLinks.forEach(function (link) {
              var isActive = link.getAttribute('href') === '#' + entry.target.id;
              link.classList.toggle('is-active', isActive);
              if (isActive) link.setAttribute('aria-current', 'true');
              else link.removeAttribute('aria-current');
            });
          });
        },
        { rootMargin: '-45% 0px -45% 0px' }
      );
      targets.forEach(function (target) {
        navObserver.observe(target);
      });
    }
  }

  /* ---------- expandable cards (hover on pointer devices, click anywhere) ---------- */
  document.querySelectorAll('[data-t-expand]').forEach(function (card) {
    var toggle = function () {
      card.classList.toggle('is-open');
      card.setAttribute('aria-expanded', card.classList.contains('is-open') ? 'true' : 'false');
    };
    card.addEventListener('click', toggle);
    card.addEventListener('keydown', function (event) {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      toggle();
    });
  });

  /* ---------- accordions ---------- */
  document.querySelectorAll('[data-t-accordion-trigger]').forEach(function (trigger) {
    var panel = document.getElementById(trigger.getAttribute('aria-controls'));
    if (!panel) return;

    panel.hidden = true;
    trigger.setAttribute('aria-expanded', 'false');

    trigger.addEventListener('click', function () {
      var opening = panel.hidden;
      panel.hidden = !opening;
      trigger.setAttribute('aria-expanded', opening ? 'true' : 'false');
      var sign = trigger.querySelector('[data-t-accordion-sign]');
      if (sign) sign.textContent = opening ? '−' : '+';
    });
  });

  /* ---------- counters ---------- */
  var counters = document.querySelectorAll('[data-t-count]');

  if (counters.length && 'IntersectionObserver' in window && !reduced) {
    var countObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var el = entry.target;
          var target = parseInt(el.getAttribute('data-t-count'), 10);
          var suffix = el.getAttribute('data-t-count-suffix') || '';
          var start = null;

          countObserver.unobserve(el);
          if (isNaN(target)) return;

          var step = function (now) {
            if (!start) start = now;
            var p = Math.min((now - start) / 900, 1);
            el.textContent = Math.round(target * (1 - Math.pow(1 - p, 3))) + suffix;
            if (p < 1) requestAnimationFrame(step);
          };
          requestAnimationFrame(step);
        });
      },
      { threshold: 0.4 }
    );
    counters.forEach(function (el) {
      countObserver.observe(el);
    });
  }

  /* ---------- filters ---------- */
  document.querySelectorAll('[data-t-filter-group]').forEach(function (group) {
    var buttons = Array.prototype.slice.call(group.querySelectorAll('[data-t-filter]'));
    var scope = document.getElementById(group.getAttribute('data-t-filter-group'));
    if (!scope) return;
    var items = Array.prototype.slice.call(scope.querySelectorAll('[data-t-filter-item]'));

    buttons.forEach(function (button) {
      button.addEventListener('click', function () {
        var value = button.getAttribute('data-t-filter');
        buttons.forEach(function (other) {
          var isActive = other === button;
          other.classList.toggle('is-active', isActive);
          other.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });
        items.forEach(function (item) {
          var show = value === 'all' || item.getAttribute('data-t-filter-item') === value;
          item.hidden = !show;
        });
      });
    });
  });

  /* ---------- tabs ---------- */
  document.querySelectorAll('[data-t-tabs]').forEach(function (tablist) {
    var tabs = Array.prototype.slice.call(tablist.querySelectorAll('[role="tab"]'));

    var select = function (index) {
      tabs.forEach(function (tab, i) {
        var isActive = i === index;
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        tab.tabIndex = isActive ? 0 : -1;
        tab.classList.toggle('is-active', isActive);
        var panel = document.getElementById(tab.getAttribute('aria-controls'));
        if (panel) panel.hidden = !isActive;
      });
    };

    tabs.forEach(function (tab, index) {
      tab.addEventListener('click', function () {
        select(index);
      });
      tab.addEventListener('keydown', function (event) {
        var moves = {
          ArrowRight: index + 1,
          ArrowDown: index + 1,
          ArrowLeft: index - 1,
          ArrowUp: index - 1,
          Home: 0,
          End: tabs.length - 1
        };
        if (!(event.key in moves)) return;
        event.preventDefault();
        var next = (moves[event.key] + tabs.length) % tabs.length;
        tabs[next].focus();
        select(next);
      });
    });

    if (tabs.length) select(0);
  });
})();
